import ClickOutsideTrap from 'components/ClickOutsideTrap';
import FocusTrapComponent from 'npm/react-focus-trap/focus-trap';
import React from 'react';

interface Props {
  /** Enable or disable all traps */
  enabled?: boolean;
  onExit?: (event: React.KeyboardEvent | React.MouseEvent | MouseEvent) => unknown;
  children: React.ReactNode;
  /** Stop ESC keypress from propagating -- useful for nested Popovers */
  stopEscPropagation?: boolean;
  shouldExitOnClickOutside?: boolean;
  shouldExitOnEsc?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const Trap: React.FC<Props> = ({
  children,
  onExit,
  enabled = true,
  stopEscPropagation = true,
  shouldExitOnClickOutside = true,
  shouldExitOnEsc = true,
  ...rest
}) => {
  const clickOutsideRef = React.createRef<HTMLDivElement>();

  const handleClickOutside = (event: React.MouseEvent | MouseEvent) => {
    if (!shouldExitOnClickOutside) {
      return;
    }

    if (!clickOutsideRef.current) {
      onExit?.(event);
      return;
    }

    const bounds = clickOutsideRef.current.getBoundingClientRect();
    if (
      event.clientX < bounds.left ||
      event.clientX > bounds.right ||
      event.clientY < bounds.top ||
      event.clientY > bounds.bottom
    ) {
      onExit?.(event);
    }
  };

  const handleExit = (event: React.KeyboardEvent) => {
    if (!shouldExitOnEsc || event.key !== 'Escape') {
      return;
    }
    if (stopEscPropagation) {
      event.stopPropagation();
    }
    onExit?.(event);
  };

  if (!enabled) {
    return <div {...rest}>{children}</div>;
  }

  return (
    <ClickOutsideTrap onClickOutside={handleClickOutside} forwardedRef={clickOutsideRef} {...rest}>
      {/* When !active, children returns directly without any wrappers */}
      <FocusTrapComponent active={shouldExitOnEsc} onExit={handleExit}>
        {children}
      </FocusTrapComponent>
    </ClickOutsideTrap>
  );
};

export default Trap;
